import React from "react";
import { ContractsManagerContext, FunctionsContext, StrategyContext, SystemSummaryContext } from "../../../global/contexts";
import { displayPct, limitDecimals } from "../../../global/utils";

const BASE_TOKEN_URL = "https://avascan.info/blockchain/c/token/";
const BASE_LEARN_MORE_URL = "https://wiki.trendespresso.com/strategies/";

export type IStrategy = {
  name: string;
  id: number;
  isPooledFund: boolean;
  description: IDescription;
};

type IDescription = {
  cash: "USDC";
  asset: "BTC.b" | "WAVAX";
  traderType: "Human" | "Automated";
  supervisor: string;
  basePctProfitShare: number;
  category: "Scalp" | "Swing" | "Macro";
  info: string[];
};

const defaultDescription: IDescription = {
  cash: "USDC",
  asset: "BTC.b",
  traderType: "Human",
  supervisor: "Trendespresso",
  basePctProfitShare: 20.00,
  category: "Swing",
  info: ["{INFO PLACEHOLDER}"],
};

const descriptions: IDescription[] = [
  // Strategy ID 0 should be null and never accessed
  defaultDescription,
  { // Strategy ID 1
    ...defaultDescription,
    traderType: "Automated",
    info: [`
      Our in-house signal generator is a game-changer and cannot be found anywhere else.
      Built with automated trading in mind, Trendespresso has carefully crafted this strategy to identify trends after initial confirmation and maintain an optimistic outlook to prevent being "wicked out" of the trade. When other automated signal generators flip back and forth aimlessly, Affogato waits for the perfect time to strike, makes its move, then patiently waits for the best exit.`,
      `This strategy falls squarely into the Trend Following category of strategies. Emphasis is put on waiting for a clear trend before making any decision.`,
    ]
  },
  { // Strategy ID 2
    ...defaultDescription,
    category: "Macro",
    info: [`
      Preserve your capital with patient deployment and maximise your chances at profitability. Our macro strategies all follow the larger market trend and can easily last months.
      If you join a macro strategy using Pooled mode, we will attempt to cycle trades quarterly to ensure deposits and withdrawals can be processed multiple times intra-year. As a reminder, Individual mode always allows for immediate deposits and immediate withdrawals.
      Macro strategies are a much more secure way to gain exposure to cryptoassets and ride the rocket 🚀`,
      `Partial entries and partial exits each over an extended period of time to average into the money at opposite ends of the market cycle are key to this strategy. This is a Macro strategy and is heavily focused on patient time-based trading. Average into a Long position at the bottom, then average into a Short position at the top.`
    ]
  },
  { // Strategy ID 3
    ...defaultDescription,
    supervisor: "The Baked Potato",
    info: [`
      Invented by The Baked Potato, the Triple Confirmation Strategy follows the momentum of the market in three easy-to-read indicators: Wolfpack MACD, Market Liberator B waves, and Potato's own in-house signal generator.
      Built with manual trading in mind, The Baked Potato has carefully crafted his strategy to identify shifting trends early and capture entries at the optimal time.
      The Triple Confirmation Strategy can be learned and employed by anyone. Or you can sit back, relax, and sip espresso while the master himself supervises the trades!`,
      `This strategy falls into the Trend Following category of strategies and can best be described as a Trend-Biased Mean Reversion strategy with occasional hedging since both Long and Short positions may be simulatenously open.`
    ]
  },
  { // Strategy ID 4
    ...defaultDescription,
    info: [`
      Using a confluence of indicators and metrics, Trendespresso can follow the trend for you with this human-traded Bitcoin swing strategy. 
      Similar to the TE Affogato strategy but with full human intervention and outside information analysis. 
      The markets move for a variety of reasons but the charts never lie. Whilst computers alone cannot analyse all outside information, 
      Trendespresso can. Meet the best of both worlds: Automated signal generation meets human filtering. ☕️`,
      `This strategy falls into the Trend Following category of strategies and can best be described as a Trend-Biased Mean Reversion strategy with emphasis on optimising the entry price.`
    ]
  },
  { // Strategy ID 5
    ...defaultDescription,
    category: "Macro",
    asset: "WAVAX",
    info: [`
      Preserve your capital with patient deployment and maximise your chances at profitability. Our macro strategies all follow the larger market trend and can easily last months.
      If you join a macro strategy using Pooled mode, we will attempt to cycle trades quarterly to ensure deposits and withdrawals can be processed multiple times intra-year. As a reminder, Individual mode always allows for immediate deposits and immediate withdrawals.
      Macro strategies are a much more secure way to gain exposure to cryptoassets and ride the rocket 🚀`,
      `Partial entries and partial exits each over an extended period of time to average into the money at opposite ends of the market cycle are key to this strategy. This is a Macro strategy and is heavily focused on patient time-based trading. Average into a Long position at the bottom, then average into a Short position at the top.`
    ]
  },
];

export const DEFAULT_STRATEGY_INDEX = 1;

export const strategies: IStrategy[] = [
  {
    name: "BTC-USD :: Triple Confirmation Strategy :: Individual",
    id: 3,
    isPooledFund: false,
    description: descriptions[3]
  },
  {
    name: "BTC-USD :: Triple Confirmation Strategy :: Pooled",
    id: 3,
    isPooledFund: true,
    description: descriptions[3]
  },
  {
    name: "BTC-USD :: TE A Swing :: Individual",
    id: 1,
    isPooledFund: false,
    description: descriptions[1]
  },
  {
    name: "BTC-USD :: TE A Swing :: Pooled",
    id: 1,
    isPooledFund: true,
    description: descriptions[1]
  },
  {
    name: "BTC-USD :: Macro :: Individual",
    id: 2,
    isPooledFund: false,
    description: descriptions[2]
  },
  {
    name: "BTC-USD :: Macro :: Pooled",
    id: 2,
    isPooledFund: true,
    description: descriptions[2]
  },
];

export function StrategyInfoSidebarElement() {
  const { SystemSummary } = React.useContext(SystemSummaryContext);
  const { contractManager } = React.useContext(ContractsManagerContext);
  const { showDialog } = React.useContext(FunctionsContext);
  const { selectedStrategy } = React.useContext(StrategyContext);

  function showStrategyDescription() {
    const titleHtml = (
      <div>
        <p className="font-h4" style={{ textAlign: "center", fontVariantCaps: "small-caps" }}>{selectedStrategy.name}</p>
        <div className="etx-card__item Card__item ph-0 pv-0">
          <hr />
        </div>
        <br />
      </div>
    );

    // ``descriptions[selectedStrategy.id].cash`` is another option
    if (selectedStrategy.description.cash !== SystemSummary.Strategy.cashSymbol) {
      console.warn(
        "selectedStrategy.description.cash DOES NOT MATCH SystemSummary.Strategy.cashSymbol:",
        selectedStrategy.description.cash,
        SystemSummary.Strategy.cashSymbol
      );
    }

    if (selectedStrategy.description.asset !== SystemSummary.Strategy.assetSymbol) {
      console.warn(
        "selectedStrategy.description.asset DOES NOT MATCH SystemSummary.Strategy.assetSymbol:",
        selectedStrategy.description.asset,
        SystemSummary.Strategy.assetSymbol
      );
    }

    const contentHtml = (
      <div>
        <div style={{ textAlign: "left" }}>
          <table style={{ width: "100%" }}>
            <tr>
              <td><b>Cash Token:</b></td>
              <a
                href={BASE_TOKEN_URL + SystemSummary.Strategy.cash}
                target="_blank"
              >
                <td>{SystemSummary.Strategy.cashSymbol}</td>
              </a>
            </tr>
            <tr>
              <td><b>Asset Token:</b></td>
              <a
                href={BASE_TOKEN_URL + SystemSummary.Strategy.asset}
                target="_blank"
              >
                <td>{SystemSummary.Strategy.assetSymbol}</td>
              </a>
            </tr>
            <tr>
              <td><b>Trader Type:</b></td>
              <td>{selectedStrategy.description.traderType}</td>
            </tr>
            <tr>
              <td><b>Supervisor:</b></td>
              <td>{selectedStrategy.description.supervisor}</td>
            </tr>
            <tr>
              <td><b>Base Profit Share:</b></td>
              <td>{displayPct(SystemSummary.Controller.settings.pctProfitShare, 2).toFixed(2)}%</td>
            </tr>
            <tr>
              <td><b>Category:</b></td>
              <td>{selectedStrategy.description.category}</td>
            </tr>
          </table>
          <br />
          {
            selectedStrategy.description.info?.map((x) => <div><p>{x}</p></div>) ?? <></>
          }
          <br />
          <p>
            <a
              href={BASE_LEARN_MORE_URL + selectedStrategy.id}
              target="_blank"
            >
              Click here to learn more
            </a>
          </p>
        </div>
      </div>
    );

    showDialog(titleHtml, contentHtml);
  }

  return (
    <div className="etx-card Card bg-white-1-20 border-radius overflow-hidden">
      <div className="etx-card__item Card__item pv-50 bg-white-1-20">
        <div className="etx-flex Flex c-auto av-center gh-50 gv-0">
          <div className="etx-flex__item Flex__item grow-1">
            <div className="etx-text Text a-center">
              {/* <h5><span style={{fontSize: "0.95em"}}>Ŧ</span>E&nbsp;&nbsp;Token</h5> */}
              <h5>Strategy&nbsp;&nbsp;Info</h5>
            </div>
          </div>
        </div>
      </div>

      <div className="etx-card__item Card__item ph-0 pv-0">
        <hr />
      </div>

      <div className="etx-card__item Card__item pv-50 strategyInfo_hover" onClick={showStrategyDescription}>
        <div className="etx-flex Flex c-auto av-center gh-50 gv-0">
          <div className="etx-flex__item Flex__item shrink-0">
            <div className="etx-text Text">
              <p className="font-label">Name</p>
            </div>
          </div>

          <div className="etx-flex__item Flex__item grow-1">
            <div className="etx-text Text a-right">
              <p className="font-size-small font-weight-medium">
                {
                  SystemSummary.Strategy.strategyInfo1
                    ? SystemSummary.Strategy.strategyInfo1
                    : "None"
                }
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="etx-card__item Card__item ph-0 pv-0">
        <hr />
      </div>

      <div className="etx-card__item Card__item pv-50 strategyInfo_hover" onClick={showStrategyDescription}>
        <div className="etx-flex Flex c-auto av-center gh-50 gv-0">
          <div className="etx-flex__item Flex__item shrink-0">
            <div className="etx-text Text">
              <p className="font-label">Supervisor</p>
            </div>
          </div>

          <div className="etx-flex__item Flex__item grow-1">
            <div className="etx-text Text a-right">
              <p className="font-size-small font-weight-medium">
                {
                  SystemSummary.Strategy.strategyInfo2
                  ? SystemSummary.Strategy.strategyInfo2.split("Managed by ")[1]
                  : "None"
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="etx-card__item Card__item ph-0 pv-0">
        <hr />
      </div>

      <div className="etx-card__item Card__item pv-50 strategyInfo_hover" onClick={showStrategyDescription}>
        <div className="etx-flex Flex c-auto av-center gh-50 gv-0">
          <div className="etx-flex__item Flex__item shrink-0">
            <div className="etx-text Text">
              <p className="font-label">Trader Type</p>
            </div>
          </div>

          <div className="etx-flex__item Flex__item grow-1">
            <div className="etx-text Text a-right">
              <p className="font-size-small font-weight-medium">
                {selectedStrategy.description.traderType}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="etx-card__item Card__item ph-0 pv-0">
        <hr />
      </div>

      <div className="etx-card__item Card__item pv-50">
        <div className="etx-flex Flex c-auto av-center gh-50 gv-0">
          <div className="etx-flex__item Flex__item shrink-0" title="Receive a 1% reduction of the Profit Fee for every Ŧ20,000 deposited.">

            <div className="etx-text Text">
              <p className="font-label">Profit Fee % <i className="far fa-info-circle"></i></p>
            </div>
          </div>

          <div className="etx-flex__item Flex__item grow-1">
            <div className="etx-text Text a-right">
              <p className="font-size-large font-weight-medium">
                {displayPct(SystemSummary.Controller.ownerInfoCurrent.pctProfitShare)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="etx-card__item Card__item ph-0 pv-0">
        <hr />
      </div>

      <div className="etx-card__item Card__item pv-50">
        <div className="etx-flex Flex c-auto av-center gh-50 gv-0">
          <div className="etx-flex__item Flex__item shrink-0">
            <div className="etx-text Text">
              <p className="font-label">Subscription Fee Per Month</p>
            </div>
          </div>

          <div className="etx-flex__item Flex__item grow-1">
            <div className="etx-text Text a-right">
              <p className="font-size-large font-weight-medium">
                <span style={{ fontSize: "0.95em" }}>Ŧ</span>{limitDecimals(SystemSummary.currentContract.subscription.feePerMonth.toNumber() / contractManager.contracts.TE.denominator.toNumber())}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="etx-card__item Card__item ph-0 pv-0">
        <hr />
      </div>

      <div className="etx-card__item Card__item pv-50">
        <div className="etx-flex Flex c-auto av-center gh-50 gv-0">
          <div className="etx-flex__item Flex__item shrink-0">
            <div className="etx-text Text">
              <p className="font-label">Subscription Paid Thru</p>
            </div>
          </div>

          <div className="etx-flex__item Flex__item grow-1">
            <div className="etx-text Text a-right">
              <p className="font-size-large font-weight-medium">
                {SystemSummary.currentContract.subscription.paidThru.gt(0) ? new Date(SystemSummary.currentContract.subscription.paidThru.toNumber() * 1000).toLocaleDateString() : "N/A"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
